import { useState } from 'react';
import Button, { ButtonSize, ButtonVariant } from '../../atoms/buttons/button/Button';
import DropdownMenu from '../../molecules/dropdown-menu/DropdownMenu';
import { formatDate, type Survey } from './types';
import { SurveyStatusBadge } from './SurveyStatusBadge';

type Props = {
  survey: Survey;
  onViewStats: (survey: Survey) => void;
  onEdit: (survey: Survey) => void;
  onDelete: (id: number) => void;
  onDuplicate: (survey: Survey) => void;
};

export function SurveyRow({ survey, onViewStats, onEdit, onDelete, onDuplicate }: Props) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  if (confirmingDelete) {
    return (
      <div className="flex items-center gap-4 px-4 py-3 border-b border-neutral-100 bg-red-50 last:border-0">
        <p className="flex-1 text-lg text-neutral-700 truncate">
          Supprimer <span className="font-medium">"{survey.theme}"</span> ? Cette action est irréversible.
        </p>
        <div className="flex shrink-0 gap-2">
          <Button
            variant={ButtonVariant.DESTRUCTIVE}
            size={ButtonSize.SM}
            onClick={() => onDelete(survey.id)}
          >
            Oui, supprimer
          </Button>
          <Button
            variant={ButtonVariant.GHOST}
            size={ButtonSize.SM}
            onClick={() => setConfirmingDelete(false)}
          >
            Annuler
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center px-4 py-3 border-b border-neutral-100 odd:bg-white even:bg-neutral-50 hover:bg-neutral-100 transition-colors last:border-0">
      <div className="flex-1 min-w-0 mr-4">
        <p className="text-lg font-medium text-neutral-800 truncate">{survey.theme}</p>
        <p className="text-lg text-neutral-400 truncate mt-0.5">{survey.description}</p>
      </div>
      <div className="w-28 shrink-0 text-lg text-neutral-600">{formatDate(survey.date_start)}</div>
      <div className="w-28 shrink-0 text-lg text-neutral-600">{formatDate(survey.date_end)}</div>
      <div className="w-28 shrink-0 text-lg text-neutral-600">{survey.question_count}</div>
      <div className="w-28 shrink-0 text-lg text-neutral-600">{survey.response_count}</div>
      <div className="w-28 shrink-0">
        <SurveyStatusBadge status={survey.status} />
      </div>
      <div className="w-16 shrink-0 flex justify-end">
        <DropdownMenu.Root modal={false}>
          <DropdownMenu.Trigger asChild>
            <Button variant={ButtonVariant.GHOST} size={ButtonSize.ICON} aria-label="Actions">
              ···
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content align="end">
            <DropdownMenu.Group>
              <DropdownMenu.Item onSelect={() => onViewStats(survey)}>
                Statistiques
              </DropdownMenu.Item>
              <DropdownMenu.Item onSelect={() => onDuplicate(survey)}>
                Dupliquer
              </DropdownMenu.Item>
              {survey.status === 'upcoming' && (
                <>
                  <DropdownMenu.Item onSelect={() => onEdit(survey)}>
                    Modifier
                  </DropdownMenu.Item>
                  <DropdownMenu.Separator />
                  <DropdownMenu.Item
                    className="text-red-500 focus:text-red-600 focus:bg-red-50"
                    onSelect={() => setConfirmingDelete(true)}
                  >
                    Supprimer
                  </DropdownMenu.Item>
                </>
              )}
            </DropdownMenu.Group>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </div>
    </div>
  );
}
